import { defineStore } from 'pinia'
import type {
  LongTermCase,
  LongTermCaseCustomData
} from '~/api/types/longTermCase'
import { deriveLongTermCaseStatus } from '~/api/types/longTermCase'
import { useCarStore } from './carStore'
import { useCustomerStore } from './customerStore'

/**
 * Parse customData from BE response (may be JSON string or object)
 */
const parseCustomData = (customData: unknown): LongTermCaseCustomData => {
  if (customData && typeof customData === 'string') {
    try {
      return JSON.parse(customData)
    } catch (e) {
      console.warn('[longTermCaseStore] Failed to parse customData:', e)
      return {}
    }
  }
  return (customData as LongTermCaseCustomData) || {}
}

/**
 * Parse a raw BE response into a LongTermCase. Normalises optional fields
 * that BE may omit (undefined) to null/false so the runtime shape matches
 * the LongTermCase interface contract.
 */
const parseLongTermCase = (raw: Record<string, unknown>): LongTermCase => ({
  ...raw as unknown as LongTermCase,
  glassInsurance: Boolean(raw.glassInsurance),
  winterTyres: Boolean(raw.winterTyres),
  vignette: Boolean(raw.vignette),
  vignetteExpirationDate: (raw.vignetteExpirationDate as number | null | undefined) ?? null,
  tyreChangingAppointment: (raw.tyreChangingAppointment as number | null | undefined) ?? null,
  leasingCompany: (raw.leasingCompany as string | null | undefined) ?? null,
  note: (raw.note as string | null | undefined) ?? null,
  customData: parseCustomData(raw.customData)
})

export const useLongTermCaseStore = defineStore({
  id: 'longTermCases',

  state: () => ({
    cases: {} as Record<string, LongTermCase>,
    loading: false,
    initialized: false
  }),

  getters: {
    allCases: (state) => Object.values(state.cases),
    activeCases(): LongTermCase[] {
      return this.allCases.filter(c => deriveLongTermCaseStatus(c.endDate) === 'ACTIVE')
    },
    endedCases(): LongTermCase[] {
      return this.allCases.filter(c => deriveLongTermCaseStatus(c.endDate) === 'ENDED')
    }
  },

  actions: {
    // ==================== Fetch Operations ====================

    async fetchCases(api: API) {
      if (!api?.rentalCase) {
        console.warn('[longTermCaseStore] api.rentalCase not available')
        return
      }

      this.loading = true

      try {
        const response = await api.rentalCase.getAllLongTermRentalCases()
        const items = (Array.isArray(response) ? response : response?.value || []) as Record<string, unknown>[]

        const newCases: Record<string, LongTermCase> = {}
        for (const item of items) {
          const entry = parseLongTermCase(item)
          if (entry.id) {
            newCases[entry.id] = entry
          }
        }

        this.cases = newCases
        this.initialized = true
      } catch (e) {
        console.error('[longTermCaseStore] Failed to fetch cases:', e)
      } finally {
        this.loading = false
      }
    },

    async get(api: API, id: string): Promise<LongTermCase | null> {
      if (!api?.rentalCase) {
        console.warn('[longTermCaseStore] api.rentalCase not available')
        return null
      }

      const raw = await api.rentalCase.getLongTermRentalCase(id)
      const entry = parseLongTermCase(raw)
      this.cases[entry.id] = entry
      await this.resolveRelations(api, [entry])
      return this.cases[entry.id]
    },

    async create(api: API, data: LongTermCase): Promise<LongTermCase | null> {
      if (!api?.rentalCase) {
        throw new Error('RentalCase API not available')
      }
      this.loading = true
      const raw = await api.rentalCase.createLongTermRentalCase(
        data.customer,
        data.rentalCar,
        data.startDate,
        data.endDate,
        data.monthlyRate,
        data.deductible,
        data.glassInsurance,
        data.winterTyres,
        data.vignette,
        data.vignetteExpirationDate,
        data.service,
        data.leasingStartDate,
        data.leasingEndDate,
        data.tyreChangingAppointment,
        data.type,
        data.leasingCompany,
        data.contractNumber,
        data.note,
        (data.customData as Record<string, unknown>) ?? null
      )
      const entry = parseLongTermCase(raw)
      this.cases[entry.id] = entry
      await this.resolveRelations(api, [entry])

      const carStore = useCarStore()
      const previousCar = data.rentalCar ? carStore.car(data.rentalCar) : null
      const previousStatus = previousCar?.status || 'AVAILABLE'
      const shortTerm = previousCar?.shortTerm ?? false

      if (data.rentalCar && previousStatus !== 'RENTED') {
        await api.cars.createRentalCarEvent(
          data.rentalCar,
          'STATUS_CHANGE',
          JSON.stringify({
            fromStatus: previousStatus,
            toStatus: 'RENTED',
            shortTerm: !!shortTerm
            }),
            Date.now(),
            null
          )
          await carStore.fetchRentalCarEvents(api, data.rentalCar).catch(() => {})
        }
        await carStore.fetchCars(api).catch(() => {})
        
        this.loading = false
        return this.cases[entry.id]
    },

    async update(api: API, id: string, updates: Partial<LongTermCase>): Promise<LongTermCase | null> {
      if (!api?.rentalCase) {
        throw new Error('RentalCase API not available')
      }

      this.loading = true

      const existing = this.cases[id]
      const merged = {
        ...existing,
        ...updates,
        customData: { ...existing?.customData, ...updates.customData }
      }

      await api.rentalCase.updateLongTermRentalCase(
        id,
        merged.customer,
        merged.rentalCar,
        merged.startDate,
        merged.endDate,
        merged.monthlyRate,
        merged.deductible,
        merged.glassInsurance,
        merged.winterTyres,
        merged.vignette,
        merged.vignetteExpirationDate,
        merged.service,
        merged.leasingStartDate,
        merged.leasingEndDate,
        merged.tyreChangingAppointment,
        merged.type,
        merged.leasingCompany,
        merged.contractNumber,
        merged.note,
        (merged.customData as Record<string, unknown>) ?? null
      )

      // Fetch fresh data from BE
      this.loading = false
      return await this.get(api, id)

    },

    async save(api: API, data: LongTermCase): Promise<LongTermCase | null> {
      return this.update(api, data.id, data)
    },

    async remove(api: API, id: string): Promise<void> {
      if (!api?.rentalCase) {
        throw new Error('RentalCase API not available')
      }
      await api.rentalCase.deleteLongTermRentalCase(id)
      delete this.cases[id]
    },

    // ==================== Relation Resolution ====================

    async resolveRelations(api: API, cases?: LongTermCase[]) {
      const casesToResolve = cases || Object.values(this.cases)
      const carStore = useCarStore()
      const customerStore = useCustomerStore()

      for (const c of casesToResolve) {
        // Resolve customer from customerStore (already loaded on dashboard mount)
        if (c.customer && !c.customerData) {
          const customer = customerStore.customer(c.customer)
          if (customer) {
            c.customerData = {
              id: customer.id,
              type: customer.type,
              name: customer.type === 'COMPANY' ? customer.company?.name : undefined,
              firstName: customer.person?.firstName,
              surname: customer.person?.surname,
              telephone: customer.person?.telephone || customer.company?.telephone,
              email: customer.person?.email || customer.company?.email,
              street: customer.person?.street || customer.company?.street,
              streetNumber: customer.person?.streetNumber || customer.company?.streetNumber,
              city: customer.person?.city || customer.company?.city,
              cityPostCode: customer.person?.cityPostCode || customer.company?.cityPostCode
            }
          }
        }

        // Resolve rental car from carStore (already loaded)
        if (c.rentalCar && !c.rentalCarData) {
          const car = carStore.car(c.rentalCar)
          if (car) {
            c.rentalCarData = {
              id: car.id,
              brand: car.brand,
              type: car.type,
              licencePlate: car.licencePlate,
              vin: car.vin,
              mileage: car.mileage,
              status: car.status,
              transmission: car.transmission,
              color: car.color,
              dailyRate: car.dailyRate,
              monthlyRate: car.monthlyRate
            }
          }
        }

        // Update in store
        this.cases[c.id] = { ...c }
      }
    }
  }
})

export type { LongTermCase } from '~/api/types/longTermCase'
